// Ручки раздела «Клиенты продавцов» (17.10.2026): /admin/seller-clients/*.
//
// Свой маленький клиент раздела, как у рекламы (ad-campaigns-api.ts): данные
// есть только на шлюзе, общий интерфейс Api консоли и его мок не трогаем.
import type {
	SellerClientAdminBind,
	SellerClientBinding,
	SellerClientSettings,
	SellerClientSettingsPatch,
	SellerClientShopTermsInput,
	SellerClientsAdminSummary,
} from "@osmix/contracts";
import { ApiClientError, createApiClient } from "@osmix/api-client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError } from "@/api/errors";
import { getAuthToken } from "@/api/token";

const readEnv = (read: () => string | undefined): string | undefined => {
	try {
		return read();
	} catch {
		return undefined;
	}
};

// Литерал целиком: Bun подставляет BUN_PUBLIC_* при сборке только так.
const GATEWAY_URL = readEnv(() => process.env.BUN_PUBLIC_API_GATEWAY_URL) ?? "";

export const sellerClientsAvailable = Boolean(GATEWAY_URL);

const client = GATEWAY_URL ? createApiClient({ baseUrl: GATEWAY_URL, getBearerToken: getAuthToken }) : null;

type Envelope<T> = { success: true; data: T } | { success: false; error?: { code?: string; message?: string } };

const call = async <T>(
	path: string,
	opts: { method?: string; query?: Record<string, string | number | undefined>; body?: unknown } = {},
): Promise<T> => {
	if (!client) throw new ApiError("Раздел доступен только при подключении к шлюзу", 503);
	try {
		const payload = await client.request<Envelope<T>>(path, opts);
		if (payload?.success) return payload.data;
		throw new ApiError(payload?.error?.message ?? "Request failed", 400, payload?.error?.code);
	} catch (error) {
		if (error instanceof ApiError) throw error;
		if (error instanceof ApiClientError) {
			const body = error.body as Envelope<T> | null;
			const err = body && body.success === false ? body.error : undefined;
			throw new ApiError(err?.message ?? `Request failed: ${error.status}`, error.status, err?.code);
		}
		throw error;
	}
};

export type BindingFilter = {
	shopId?: string;
	status?: "active" | "ended" | "all";
	grace?: "in" | "out" | "all";
	q?: string;
	from?: string;
	to?: string;
};

export const sellerClientsApi = {
	settings: () => call<SellerClientSettings>("/admin/seller-clients/settings"),
	updateSettings: (patch: SellerClientSettingsPatch) =>
		call<SellerClientSettings>("/admin/seller-clients/settings", { method: "PUT", body: patch }),
	summary: (range: { from?: string; to?: string }) =>
		call<SellerClientsAdminSummary>("/admin/seller-clients/summary", { query: range }),
	bindings: (f: BindingFilter) =>
		call<SellerClientBinding[]>("/admin/seller-clients/bindings", {
			query: {
				shopId: f.shopId || undefined,
				status: f.status ?? "active",
				grace: f.grace && f.grace !== "all" ? f.grace : undefined,
				q: f.q || undefined,
				from: f.from || undefined,
				to: f.to || undefined,
			},
		}),
	shopOptions: () => call<{ shopId: string; shopName: string }[]>("/admin/seller-clients/shop-options"),
	bind: (input: SellerClientAdminBind) =>
		call<SellerClientBinding>("/admin/seller-clients/bindings", { method: "POST", body: input }),
	unbind: (id: string, reason: string) =>
		call<SellerClientBinding>(`/admin/seller-clients/bindings/${encodeURIComponent(id)}/unbind`, {
			method: "POST",
			body: { reason },
		}),
	setTerms: (shopId: string, input: SellerClientShopTermsInput) =>
		call<{ shopId: string; graceMonths: number | null; note: string }>(
			`/admin/seller-clients/shops/${encodeURIComponent(shopId)}/terms`,
			{ method: "PUT", body: input },
		),
};

const KEY = ["admin-seller-clients"] as const;

export const useSellerClientSettings = () =>
	useQuery({ queryKey: [...KEY, "settings"], queryFn: sellerClientsApi.settings, enabled: sellerClientsAvailable });

export const useSellerClientSummary = (range: { from?: string; to?: string }) =>
	useQuery({
		queryKey: [...KEY, "summary", range.from, range.to],
		queryFn: () => sellerClientsApi.summary(range),
		enabled: sellerClientsAvailable,
	});

export const useSellerClientBindings = (f: BindingFilter) =>
	useQuery({
		queryKey: [...KEY, "bindings", f.shopId ?? "", f.status ?? "active", f.grace ?? "all", f.q ?? "", f.from ?? "", f.to ?? ""],
		queryFn: () => sellerClientsApi.bindings(f),
		enabled: sellerClientsAvailable,
	});

export const useSellerClientShopOptions = () =>
	useQuery({
		queryKey: [...KEY, "shop-options"],
		queryFn: sellerClientsApi.shopOptions,
		enabled: sellerClientsAvailable,
		staleTime: 5 * 60_000,
	});

export const useSellerClientMutations = () => {
	const queryClient = useQueryClient();
	const refresh = () => queryClient.invalidateQueries({ queryKey: KEY });
	return {
		saveSettings: useMutation({
			mutationFn: (patch: SellerClientSettingsPatch) => sellerClientsApi.updateSettings(patch),
			onSuccess: refresh,
		}),
		bind: useMutation({ mutationFn: (input: SellerClientAdminBind) => sellerClientsApi.bind(input), onSuccess: refresh }),
		unbind: useMutation({
			mutationFn: ({ id, reason }: { id: string; reason: string }) => sellerClientsApi.unbind(id, reason),
			onSuccess: refresh,
		}),
		setTerms: useMutation({
			mutationFn: ({ shopId, input }: { shopId: string; input: SellerClientShopTermsInput }) =>
				sellerClientsApi.setTerms(shopId, input),
			onSuccess: refresh,
		}),
	};
};

const rubFormat = new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 2 });
export const rub = (value: number) => `${rubFormat.format(value)} руб.`;
export const pct = (value: number) =>
	`${new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 2 }).format(value * 100)} %`;
export const dateOnly = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString("ru-RU") : "-");
export const dateTime = (iso: string | null) =>
	iso ? new Date(iso).toLocaleString("ru-RU", { dateStyle: "short", timeStyle: "short" }) : "-";

/** «3 месяца», «1 месяц», «5 месяцев». */
export const monthsLabel = (n: number) => {
	const mod10 = n % 10;
	const mod100 = n % 100;
	const word = mod10 === 1 && mod100 !== 11 ? "месяц" : mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14) ? "месяца" : "месяцев";
	return `${n} ${word}`;
};

/** Окно отчёта по Москве: последние N дней включая сегодня. */
export const lastDays = (days: number): { from: string; to: string } => {
	const msk = (offset: number) => new Date(Date.now() + 3 * 3_600_000 - offset * 86_400_000).toISOString().slice(0, 10);
	return { from: msk(days - 1), to: msk(0) };
};
